import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

// AI training reports of the company — on the whole team, or on one staff member. They
// are generated automatically every night by the backend (no "Generate" button): the
// admin and the manager only read them. `status` = next reports planned and the latest
// skipped ones with their reason, computed server-side.

const initialState = {
  team: {
    status: null,
    reports: [],
    loading: false,
    error: null // backend error code
  },
  // Every current staff member at once (the list and the summary cards): reports so far,
  // next report, ratings collected for it, and whether last month was rated.
  overview: {
    data: null,
    loading: false,
    error: null
  },
  // One staff member at a time (the report view). `employeeId` guards against a slow
  // response for a previously opened staff member overwriting the current one.
  staff: {
    employeeId: null,
    status: null,
    reports: [],
    loading: false,
    error: null
  }
};

const errorCode = (error) => {
  const message = error.response?.data?.message;
  return typeof message === 'string' ? message : 'GenericError';
};

const request =
  (fn) =>
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      return await fn(arg);
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(errorCode(error));
    }
  };

export const fetchTeamTraining = createAsyncThunk(
  'staffTrainingReport/fetchTeam',
  request(async () => {
    const [status, reports] = await Promise.all([
      api.get('/staff-training-report/company/team/status'),
      api.get('/staff-training-report/company/team')
    ]);
    return { status: status.data, reports: reports.data };
  })
);

export const fetchStaffOverview = createAsyncThunk(
  'staffTrainingReport/fetchOverview',
  request(async () => {
    const response = await api.get(
      '/staff-training-report/company/staff-overview'
    );
    return response.data;
  })
);

export const fetchStaffTraining = createAsyncThunk(
  'staffTrainingReport/fetchStaff',
  request(async (employeeId) => {
    const [status, reports] = await Promise.all([
      api.get(`/staff-training-report/company/staff/${employeeId}/status`),
      api.get(`/staff-training-report/company/staff/${employeeId}`)
    ]);
    return { status: status.data, reports: reports.data };
  })
);

const staffTrainingReportSlice = createSlice({
  name: 'staffTrainingReport',
  initialState,
  reducers: {
    openStaffTraining: (state, { payload: employeeId }) => {
      state.staff = { ...initialState.staff, employeeId, loading: true };
    },
    closeStaffTraining: (state) => {
      state.staff = initialState.staff;
    },
    clearTeamTrainingMessages: (state) => {
      state.team.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamTraining.pending, (state) => {
        state.team.loading = true;
      })
      .addCase(fetchTeamTraining.fulfilled, (state, { payload }) => {
        state.team.loading = false;
        state.team.status = payload.status;
        state.team.reports = payload.reports || [];
      })
      .addCase(fetchTeamTraining.rejected, (state, { payload }) => {
        state.team.loading = false;
        state.team.error = payload;
      })

      .addCase(fetchStaffOverview.pending, (state) => {
        state.overview.loading = true;
        state.overview.error = null;
      })
      .addCase(fetchStaffOverview.fulfilled, (state, { payload }) => {
        state.overview.loading = false;
        state.overview.data = payload;
      })
      .addCase(fetchStaffOverview.rejected, (state, { payload }) => {
        state.overview.loading = false;
        state.overview.error = payload;
      })

      .addCase(fetchStaffTraining.fulfilled, (state, { payload, meta }) => {
        if (meta.arg !== state.staff.employeeId) return;
        state.staff.loading = false;
        state.staff.status = payload.status;
        state.staff.reports = payload.reports || [];
      })
      .addCase(fetchStaffTraining.rejected, (state, { payload, meta }) => {
        if (meta.arg !== state.staff.employeeId) return;
        state.staff.loading = false;
        state.staff.error = payload;
      });
  }
});

export const {
  openStaffTraining,
  closeStaffTraining,
  clearTeamTrainingMessages
} = staffTrainingReportSlice.actions;

export default staffTrainingReportSlice.reducer;
